import { supabase } from '@/lib/supabase';
import { AuthError } from '@supabase/supabase-js';

class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export const authService = {
  login: async (email: string, password: string) => {
    try {
      console.log('Attempting login...', { email });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log('Auth response:', { data, error });

      if (error) {
        console.error('Auth error:', error);
        if (error instanceof AuthError) {
          switch (error.message) {
            case 'Invalid login credentials':
              throw new AuthenticationError('Invalid email or password');
            case 'Email not confirmed':
              throw new AuthenticationError('Please verify your email first');
            default:
              throw new AuthenticationError(error.message);
          }
        }
        throw error;
      }

      if (!data.user || !data.session) {
        console.error('No user or session in response');
        throw new AuthenticationError('Login failed. Please try again.');
      }

      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token
      });

      console.log('Login successful:', { user: data.user });
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  signup: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    });
    if (error) throw error;
    return data;
  },

  logout: async () => {
    try {
      // Create a silent version of signOut that won't throw or log errors
      const silentSignOut = async () => {
        try {
          // Use a timeout to prevent hanging if the request takes too long
          const signOutPromise = supabase.auth.signOut();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Signout timed out')), 2000)
          );
          
          await Promise.race([signOutPromise, timeoutPromise]);
          return { success: true };
        } catch (e) {
          // Silently fail - don't log or propagate errors
          return { success: false };
        }
      };
      
      // Try the silent signout
      await silentSignOut();
      
      // Force client-side session clearing
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.removeItem('supabase.auth.token');
      
      // Clear any other auth-related storage
      const localStorageKeys = Object.keys(localStorage);
      const sessionStorageKeys = Object.keys(sessionStorage);
      
      localStorageKeys.forEach(key => {
        if (key.includes('supabase.auth') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      sessionStorageKeys.forEach(key => {
        if (key.includes('supabase.auth') || key.includes('sb-')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Don't rethrow - ensure the UI can still proceed with logout flow
    }
  },

  resetPassword: async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
  },

  updatePassword: async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    if (error) throw error;
  }
}; 